import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export const TWITTER_URL = "https://x.com/hoodrankio";

type NavItem = {
  label: string;
  to?: string;
  href?: string;
  external?: boolean;
  params?: Record<string, string>;
};

const NAV: NavItem[] = [
  { label: "Ranking", to: "/app" },
  { label: "How it works", href: "/#how" },
  { label: "Trending", to: "/trending" },
  { label: "Leaderboard", to: "/leaderboard" },
  { label: "Docs", to: "/docs" },
  { label: "Twitter", href: TWITTER_URL, external: true },
];

function Wordmark() {
  return (
    <Link
      to="/"
      className="flex shrink-0 items-center text-[18px] leading-none font-display tracking-tight sm:text-[20px]"
    >
      <span className="text-ink">HOOD</span>
      <span className="ml-1 bg-accent px-1.5 py-0.5 text-accent-ink">RANK</span>
    </Link>
  );
}

function NavLinks({
  onNavigate,
  className,
  itemClassName = "navlink py-1",
}: {
  onNavigate?: () => void;
  className?: string;
  itemClassName?: string;
}) {
  return (
    <nav aria-label="Main" className={className}>
      {NAV.map((n) =>
        n.to ? (
          <Link
            key={n.label}
            to={n.to}
            params={n.params as never}
            onClick={onNavigate}
            className={itemClassName}
          >
            {n.label}
          </Link>
        ) : (
          <a
            key={n.label}
            href={n.href}
            onClick={onNavigate}
            {...(n.external ? { target: "_blank", rel: "noreferrer noopener" } : {})}
            className={itemClassName}
          >
            {n.label}
          </a>
        ),
      )}
    </nav>
  );
}

/** Shared site header: full nav on desktop, sheet menu on mobile. */
export function SiteHeader() {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-bg">
      <div className="shell flex h-16 items-center justify-between gap-3 md:h-18">
        <Wordmark />

        <NavLinks className="hidden items-center gap-7 lg:flex" />

        <div className="flex shrink-0 items-center gap-3">
          <Link
            to="/app"
            className="btn btn-fill !px-3 !py-2.5 !text-[11px] sm:!px-5 sm:!py-3 sm:!text-[13px]"
          >
            <span className="hidden sm:inline">Check rank</span>
            <span className="sm:hidden">Rank</span> →
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              aria-label="Open menu"
              className="flex size-10 items-center justify-center border border-ink text-ink lg:hidden"
            >
              <span className="flex w-4 flex-col gap-[3px]" aria-hidden="true">
                <span className="h-[2px] w-full bg-ink" />
                <span className="h-[2px] w-full bg-ink" />
                <span className="h-[2px] w-full bg-ink" />
              </span>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[86vw] max-w-[340px] border-l border-ink bg-bg !rounded-none"
            >
              <SheetTitle className="label">Menu</SheetTitle>
              <NavLinks
                onNavigate={() => setOpen(false)}
                className="mt-8 flex flex-col"
                itemClassName="navlink border-b border-line py-4 !text-[14px]"
              />
              <Link to="/app" onClick={() => setOpen(false)} className="btn btn-dark mt-8 w-full">
                Check rank →
              </Link>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
